import { useState, useMemo, useRef } from "react";
import { Occurrence } from "../../../models/occurrence";
import SummaryDialog from "../dialogs/summaryDialog";
import {
  convertTimeStringToMilliseconds,
  formatMilisecondsInDaysHoursMinutes,
} from "../../../utils/dateUtils";
import { OccurrenceStepEnum } from "../../../enums/occurrenceStep";
import { SimpleTooltip } from "@rio-cloud/rio-uikit";
import { useMediaQuery } from "@mui/material";
import { useCurrentStep } from "../../../hooks/useGetCurrentStep";
import { OccurrenceTypeLabelEnum } from "../../../enums/occurrenceType";
import VehicleIcon from "../../VehicleIcon";

interface IVehicleCardProps {
  occurrence: Occurrence;
  index: number;
  isValidStep: (requiredFields: string[]) => boolean;
  loading: boolean;
  save: () => Promise<boolean>;
  disabledAllFields?: boolean;
  persona: "MANAGER" | "TOWER" | "CONSULTANT" | "PARTNERSHIP";
  finalized?: boolean;
  occurrenceStepAvailable: string[];
  partners?: { id: string; name: string }[];
  changeStep?: (
    occurrence: Occurrence,
    fromStepId: number,
    to: OccurrenceStepEnum
  ) => Promise<Occurrence | null>;
  getNotFilledRequiredFields: (
    requiredFields: string[],
    occurrence: Occurrence
  ) => string[];
}

export const VehicleCard = (props: IVehicleCardProps) => {
  const [showSummaryDialog, setShowSummaryDialog] = useState(false);
  const [fullOccurrence, setFullOccurrence] = useState<Occurrence | null>(null);
  const hasLoadedRef = useRef(false);
  const isMobile = useMediaQuery("(max-width: 768px)");
  const currentStep = useCurrentStep(props.occurrence.steps ?? []);
  const steps = props.occurrence.steps ?? [];
  const hasNoSteps = steps.length === 0;
  const isFinalized = Boolean(props.finalized);
  const statusLabel = isFinalized
    ? "Concluído"
    : currentStep?.isInProgress
    ? currentStep.label
    : hasNoSteps
    ? "Não iniciado"
    : currentStep?.label || "";

  const criticalityColorMap = {
    5: "bg-red-500",
    4: "bg-orange-400",
    3: "bg-yellow-500",
    2: "bg-green-500",
    1: "bg-blue-500",
  };

  const criticalityLabelMap = {
    5: "Emergência",
    4: "Muito urgente",
    3: "Urgente",
    2: "Pouco urgente",
    1: "Não urgente",
  };

  const getAssetModel = (occurrence: Occurrence) => {
    if(occurrence.vehicleName && (occurrence.vehicleName !== '-')) return occurrence.vehicleName;
    return occurrence.vehicleModel;
  }

  // Função para calcular o tempo total somando todos os steps (incluindo OKCLI e RELEASE)
  const totalTimeFromAllSteps = useMemo(() => {
    if (!steps || steps.length === 0) {
      return 0;
    }

    try {
      const now = Date.now();
      const getDurationMs = (start?: Date | null, end?: Date | null) => {
        if (!start) return 0;
        const endMs = end ? end.getTime() : now;
        return Math.max(0, endMs - start.getTime());
      };

      // Soma todos os steps, incluindo OKCLI e RELEASE
      const totalMs = steps
        .filter((step) => step.dtStart)
        .reduce((acc, step) => {
          const stepTime = getDurationMs(step.dtStart, step.dtEnd);
          return acc + stepTime;
        }, 0);

      return totalMs;
    } catch (error) {
      console.error("Erro ao calcular tempo total dos steps:", error);
      return 0;
    }
  }, [steps]);

  return (
    <>
      <div className="w-[300px]">
        <div
          onClick={() => setShowSummaryDialog(true)}
          className="p-[10px] cursor-pointer bg-[#C7D1D9] dark:bg-[#1a1a1a] border-b-[16px] border-b-white dark:border-b-[#1a1a1a]"
        >
          <div
            key={props.index}
            className={`border rounded-lg p-4 h-[225px] bg-white dark:bg-[#1a1a1a] select-none ${
              isMobile ? "w-[300px]" : ""
            }`}
            style={{ 
              borderWidth: "1.7px", 
              borderColor: "#31b5c0"
            }}
          >
            <div className="flex items-center justify-between pb-2">
              <div className="flex items-center space-x-2">
                <VehicleIcon
                  chassis={props.occurrence.vehicleChassis}
                  size={20}
                  className=""
                />
                <span className="font-semibold text-[14px] truncate max-w-[100px] select-none">
                  {getAssetModel(props.occurrence)}
                </span>
              </div>
              <div className="flex items-center space-x-1">
                <span
                  className={`w-3 h-3 rounded-full ${
                    criticalityColorMap[
                      props.occurrence
                        .criticality as unknown as keyof typeof criticalityColorMap
                    ]
                  }`}
                ></span>
                <span className="text-gray-700 dark:text-white text-[14px] pl-1 select-none">
                  {
                    criticalityLabelMap[
                      props.occurrence
                        .criticality as unknown as keyof typeof criticalityLabelMap
                    ]
                  }
                </span>
              </div>
            </div>

            <hr style={{ margin: 0 }} />

            {/* Dados principais */}
            <div className="py-2 space-y-2 text-[14px]">
              <div className="flex justify-between text-gray-700 dark:text-white">
                <span className="select-none">{props.occurrence.vehicleChassis}</span>
                <span className="select-none">{props.occurrence.vehicleLicensePlate}</span>
              </div>
              {props.occurrence.contractNumber && (
                <div className="flex justify-start text-gray-700">
                  <span className="select-none font-semibold" style={{ color: '#0066cc' }}>
                    Contrato: {props.occurrence.contractNumber}
                  </span>
                </div>
              )}
              <div className="flex justify-between text-gray-700">
                <span className="select-none">{props.occurrence.vehicleOdometer}</span>
                <span className="select-none">{props.occurrence.vehicleHourmeter}</span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-gray-700 dark:text-white">Tempo estimado:</span>
                <div className="flex items-center space-x-1">
                  <span className="rioglyph rioglyph-time-alt text-gray-700 dark:text-white" />
                  <span className="text-gray-700 dark:text-white select-none">
                    {formatMilisecondsInDaysHoursMinutes(
                      (props.occurrence.steps ?? [])
                        .map((step) =>
                          convertTimeStringToMilliseconds(
                            step.estimatedTime ?? "00:00"
                          )
                        )
                        .reduce((acc, curr) => acc + curr, 0)
                    )}
                  </span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-700 dark:text-white">Tempo total:</span>
                <div className="flex items-center space-x-1 font-bold">
                  <span className="rioglyph rioglyph-time-alt" />
                  <span className="select-none">
                    {formatMilisecondsInDaysHoursMinutes(totalTimeFromAllSteps)}
                  </span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <div className="truncate max-w-[120px]">
                  <SimpleTooltip
                    content={props.occurrence.accountName}
                    placement="top"
                  >
                    <span className="text-gray-700 dark:text-white select-none">{props.occurrence.accountName}</span>
                  </SimpleTooltip>
                </div>
                <div className="truncate max-w-[120px] pl-4">
                  <SimpleTooltip
                    content={
                      props.occurrence.serviceBaySchedule?.serviceBay?.name
                    }
                    placement="top"
                  >
                    <span className="text-gray-700 dark:text-white select-none">
                      {props.occurrence.serviceBaySchedule?.serviceBay?.name}
                    </span>
                  </SimpleTooltip>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-700 dark:text-white">Concessionária:</span>
                <div className="flex items-center space-x-1">
                  <SimpleTooltip
                    content={`${props.occurrence.dealershipName} - DN: ${props.occurrence.dealershipDn}`}
                    placement="top"
                  >
                    <span className="truncate max-w-[120px] text-gray-700 dark:text-white select-none">
                      {props.occurrence.dealershipName} - DN: {props.occurrence.dealershipDn}
                    </span>
                  </SimpleTooltip>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-700 dark:text-white">Etapa atual:</span>
                <div className="flex items-center space-x-1">
                  <SimpleTooltip
                    content={statusLabel}
                    placement="top"
                  >
                    <span className="truncate max-w-[120px] select-none">
                      {statusLabel}
                    </span>
                  </SimpleTooltip>
                </div>
              </div>
              {props.occurrence.occurrenceType && (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-md text-gray-600">Tipo de ocorrência:</span>
                  <SimpleTooltip
                    content={OccurrenceTypeLabelEnum[props.occurrence.occurrenceType as unknown as keyof typeof OccurrenceTypeLabelEnum] ?? props.occurrence.occurrenceType ?? "Não informado"}
                    placement="top"
                  >
                    <span className="text-md font-medium select-none truncate max-w-[120px]">
                      {OccurrenceTypeLabelEnum[props.occurrence.occurrenceType as unknown as keyof typeof OccurrenceTypeLabelEnum] ?? props.occurrence.occurrenceType ?? "Não informado"}
                    </span>
                  </SimpleTooltip>
                </div>
              </>
            )}
            </div>
          </div>
        </div>
      </div>

      <SummaryDialog
        showDialog={showSummaryDialog}
        onClose={() => setShowSummaryDialog(false)}
        occurrence={props.occurrence}
        isValidStep={props.isValidStep}
        loading={props.loading}
        save={props.save}
        disabledAllFields={props.disabledAllFields}
        persona={props.persona}
        finalized={props.finalized}
        occurrenceStepAvailable={props.occurrenceStepAvailable}
        partners={props.partners}
        changeStep={props.changeStep}
        getNotFilledRequiredFields={props.getNotFilledRequiredFields}
      />
    </>
  );
};

export const SkeletonVehicleCard = (props: { index: number }) => {
  const isMobile = useMediaQuery("(max-width: 768px)");

  return (
    <div className="w-[300px]">
      <div
        className="p-[10px] cursor-pointer"
        style={{
          backgroundColor: "#C7D1D9",
          borderBottom: "16px solid #FFFFFF",
        }}
      >
        <div
          key={props.index}
          className={`border border-black rounded-lg p-4 bg-white h-[205px] ${
            isMobile ? "w-[300px]" : ""
          }`}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2 mb-2">
              <div className="h-[20px] w-[20px] animate-pulse rounded bg-gray-200 mb-2"></div>
              <div className="h-[20px] w-[100px] animate-pulse rounded bg-gray-200 mb-2"></div>
            </div>
            <div className="flex items-center space-x-1 mb-2">
              <div className="h-[12px] w-[12px] animate-pulse rounded-full bg-gray-200 mb-2"></div>
              <div className="h-[14px] w-[100px] animate-pulse rounded bg-gray-200 mb-2"></div>
            </div>
          </div>

          <hr style={{ margin: 0 }} />

          <div className="py-2 space-y-2 text-[14px] mb-2">
            <div className="flex justify-between text-gray-700">
              <div className="h-[14px] w-[100px] animate-pulse rounded bg-gray-200 mb-2"></div>
              <div className="h-[14px] w-[100px] animate-pulse rounded bg-gray-200 mb-2"></div>
            </div>
            <div className="flex justify-between text-gray-700 mb-2">
              <div className="h-[14px] w-[100px] animate-pulse rounded bg-gray-200 mb-2"></div>
              <div className="h-[14px] w-[100px] animate-pulse rounded bg-gray-200 mb-2"></div>
            </div>

            <div className="flex justify-between items-center mb-2">
              <div className="h-[14px] w-[100px] animate-pulse rounded bg-gray-200 mb-2"></div>
              <div className="flex items-center space-x-1">
                <div className="h-[14px] w-[14px] animate-pulse rounded bg-gray-200 mb-2"></div>
                <div className="h-[14px] w-[100px] animate-pulse rounded bg-gray-200 mb-2"></div>
              </div>
            </div>
            <div className="flex justify-between items-center mb-2">
              <div className="h-[14px] w-[100px] animate-pulse rounded bg-gray-200 mb-2"></div>
              <div className="flex items-center space-x-1 font-bold">
                <div className="h-[14px] w-[14px] animate-pulse rounded bg-gray-200 mb-2"></div>
                <div className="h-[14px] w-[100px] animate-pulse rounded bg-gray-200 mb-2"></div>
              </div>
            </div>
            <div className="flex justify-between items-center mb-2">
              <div className="h-[14px] w-[100px] animate-pulse rounded bg-gray-200 mb-2"></div>
              <div className="flex items-center space-x-1 font-bold">
                <div className="h-[14px] w-[14px] animate-pulse rounded bg-gray-200 mb-2"></div>
                <div className="h-[14px] w-[100px] animate-pulse rounded bg-gray-200 mb-2"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
